import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  MenuBook as MenuBookFilledIcon,
  MonetizationOn as MonetizationIcon,
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useLocation } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { DEFAULT_AVATAR } from 'src/assets';
import type { MenuItem } from 'src/components/common';
import { ItemDetailsLayout, RedirectPage } from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUser, useUserAvatar } from 'src/hooks/user';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import { SxProp } from 'src/types/mui';
import { getIsPathMatchLocation } from 'src/utils/router';
import { getUserFullName } from 'src/utils/user';

export const AccountLayoutVerifierAdmin: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { isMobile } = useScreenInfo();

  const { id: userId, user } = useCurrentUser();
  const { verifierStaffBillingAccess, loading } = useCurrentUserVerifierStaff();
  const { userAvatar, userAvatarLoading, updateUserAvatar, userAvatarUpdating } =
    useUserAvatar(userId);

  const fullName = getUserFullName(user);
  const title = user?.title;
  const hasAccessToBilling = verifierStaffBillingAccess?.verifier.type === 'Commercial';

  const getIsCurrentPath = React.useCallback(
    (path: string) => {
      return getIsPathMatchLocation(location, path);
    },
    [location],
  );

  const menuItems: Nullable<MenuItem>[] = React.useMemo(
    () => [
      {
        link: appRoutes.account.personalInformation,
        title: 'Personal Information',
        icon: PersonIcon,
      },
      {
        link: appRoutes.account.companyInformation,
        title: 'Company Information',
        icon: AccountBalanceIcon,
      },
      hasAccessToBilling
        ? {
            link: appRoutes.account.billing,
            title: 'Billing',
            icon: MonetizationIcon,
          }
        : null,
      {
        link: appRoutes.account.verificationPurposes,
        title: 'Verification Purposes',
        icon: MenuBookFilledIcon,
      },
      {
        link: appRoutes.account.notifications,
        title: 'Notifications',
        icon: NotificationsIcon,
        divider: true,
      },
      {
        link: appRoutes.account.security,
        title: 'Sign-in & Security',
        icon: PasswordIcon,
      },
      {
        link: appRoutes.account.setupMfa,
        title: 'Multi-factor Authentication',
        icon: PhonelinkIcon,
      },
    ],
    [hasAccessToBilling],
  );

  if (!loading && getIsCurrentPath(appRoutes.account.billing) && !hasAccessToBilling) {
    return <RedirectPage />;
  }

  if (isMobile) {
    return <Box sx={mobileContentWrapperStyles}>{children}</Box>;
  }

  return (
    <ItemDetailsLayout
      name={fullName}
      subtitle={title}
      logoUrl={userAvatar?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updateUserAvatar,
        loading: userAvatarLoading || userAvatarUpdating,
      }}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};

const mobileContentWrapperStyles: SxProp = {
  zIndex: '1',
  position: 'relative',
  height: '100%',
  width: '100%',
  overflowY: 'scroll',
};
